import { AnnouncementContentPipe } from "./announcement-content.pipe"
import { DatePipe } from "@angular/common"
import { Component, computed, inject, signal } from "@angular/core"
import { MatIconModule } from "@angular/material/icon"
import { catchError, finalize, forkJoin, of, takeUntil, Subject, switchMap, timer, Subscription } from "rxjs"
import { AppBarComponent } from "../../../../shared/components/app-bar/app-bar.component"
import { MainContentComponent } from "../../../../shared/components/main-content/main-content.component"
import { SeoComponent } from "../../../../shared/components/seo/seo.component"
import { ISpot } from "../../../../spots/interfaces/spot-recommendation.interface"
import {
  IAnnouncement,
  IIssue,
  IIssueReportResponse,
  IIssueType,
} from "../../../interfaces/hub.interface"
import { HubService } from "../../../services/hub.service"

@Component({
  selector: "app-hub",
  imports: [AnnouncementContentPipe, DatePipe, MatIconModule, AppBarComponent, MainContentComponent],
  templateUrl: "./hub.component.html",
  styleUrl: "./hub.component.scss",
})
export class HubComponent extends SeoComponent {
  private readonly hubService = inject(HubService)

  private readonly announcementRequests = new Subject<number>()
  private issueRequest?: Subscription

  readonly announcements = signal<IAnnouncement[]>([])
  readonly announcementsLoading = signal(true)
  readonly announcementsError = signal(false)
  readonly activeCategory = signal<number | "all">("all")
  readonly searchQuery = signal("")

  readonly reportOpen = signal(false)
  readonly reportOptionsLoading = signal(false)
  readonly reportOptionsError = signal(false)
  readonly issueTypes = signal<IIssueType[]>([])
  readonly issues = signal<IIssue[]>([])
  readonly spots = signal<ISpot[]>([])
  readonly issuesLoading = signal(false)
  readonly selectedIssueTypeId = signal<number | null>(null)
  readonly selectedIssueId = signal<number | null>(null)
  readonly selectedSpotId = signal<number | null>(null)
  readonly reportComment = signal("")
  readonly submitting = signal(false)
  readonly submitError = signal(false)
  readonly submittedReport = signal<IIssueReportResponse | null>(null)

  readonly pinnedAnnouncements = computed(() =>
    this.announcements().filter((item) => item.pinned && this.matchesCategory(item)),
  )
  private readonly knownCategories = signal<{ id: number; name: string }[]>([])
  readonly categoryFilters = computed(() => {
    const categories = new Map<number, string>()
    this.knownCategories().forEach((category) => categories.set(category.id, category.name))
    return [...categories].map(([id, label]) => ({ id, label }))
  })
  readonly filteredAnnouncements = computed(() => {
    return this.announcements().filter((item) => !item.pinned && this.matchesCategory(item))
  })

  private matchesCategory(item: IAnnouncement): boolean {
    return this.activeCategory() === "all" || item.categories.some((tag) => tag.id === this.activeCategory())
  }

  readonly canSubmit = computed(
    () =>
      this.selectedSpotId() != null &&
      this.selectedIssueTypeId() != null &&
      this.selectedIssueId() != null &&
      !this.submitting(),
  )

  constructor() {
    super()
    this.title = "CTBE"
    this.announcementRequests.pipe(
      switchMap((delay) => {
        this.announcementsLoading.set(true)
        this.announcementsError.set(false)
        return timer(delay).pipe(
          switchMap(() => this.hubService.getAnnouncements(50, this.searchQuery())),
          catchError(() => {
            this.announcementsError.set(true)
            return of([])
          }),
          finalize(() => this.announcementsLoading.set(false)),
        )
      }),
      takeUntil(this.destroyed$),
    ).subscribe((announcements) => {
      this.announcements.set(announcements)
      const categories = new Map(this.knownCategories().map((category) => [category.id, category]))
      announcements.forEach((item) => item.categories.forEach((category) => categories.set(category.id, category)))
      this.knownCategories.set([...categories.values()])
    })
    this.loadAnnouncements()
  }

  loadAnnouncements(): void {
    this.announcementRequests.next(0)
  }

  setCategory(category: number | "all"): void {
    this.activeCategory.set(category)
  }

  onSearch(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value)
    this.announcementRequests.next(300)
  }

  openReport(): void {
    this.reportOpen.set(true)
    this.submittedReport.set(null)
    if (!this.issueTypes().length || !this.spots().length) this.loadReportOptions()
  }

  closeReport(): void {
    this.issueRequest?.unsubscribe()
    this.reportOpen.set(false)
    this.selectedIssueTypeId.set(null)
    this.selectedIssueId.set(null)
    this.selectedSpotId.set(null)
    this.issues.set([])
    this.reportComment.set("")
    this.submitError.set(false)
  }

  loadReportOptions(): void {
    this.reportOptionsLoading.set(true)
    this.reportOptionsError.set(false)
    forkJoin({ issueTypes: this.hubService.getIssueTypes(), spots: this.hubService.getSpots() })
      .pipe(
        catchError(() => {
          this.reportOptionsError.set(true)
          return of(null)
        }),
        finalize(() => this.reportOptionsLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((result) => {
        if (!result) return
        this.issueTypes.set(result.issueTypes)
        this.spots.set(result.spots)
      })
  }

  selectIssueType(issueTypeId: number): void {
    this.issueRequest?.unsubscribe()
    this.issuesLoading.set(false)
    this.submitError.set(false)
    if (!issueTypeId) {
      this.selectedIssueTypeId.set(null)
      this.selectedIssueId.set(null)
      this.issues.set([])
      return
    }
    this.selectedIssueTypeId.set(issueTypeId)
    this.selectedIssueId.set(null)
    this.issues.set([])
    this.issuesLoading.set(true)
    this.issueRequest = this.hubService
      .getIssues(issueTypeId)
      .pipe(
        catchError(() => {
          this.submitError.set(true)
          return of([])
        }),
        finalize(() => this.issuesLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((issues) => this.issues.set(issues))
  }

  onCommentChange(event: Event): void {
    this.reportComment.set((event.target as HTMLTextAreaElement).value)
  }

  submitReport(): void {
    const spotId = this.selectedSpotId()
    const issueTypeId = this.selectedIssueTypeId()
    const issueId = this.selectedIssueId()
    if (spotId == null || issueTypeId == null || issueId == null) return

    this.submitting.set(true)
    this.submitError.set(false)
    const description = this.reportComment().trim()
    this.hubService
      .submitIssueReport({
        spotId,
        issueTypeId,
        issueId,
        ...(description ? { description } : {}),
      })
      .pipe(
        catchError(() => {
          this.submitError.set(true)
          return of(null)
        }),
        finalize(() => this.submitting.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((report) => {
        if (!report) return
        this.submittedReport.set(report)
        setTimeout(() => this.closeReport(), 2200)
      })
  }

  categoryNames(item: IAnnouncement): string {
    return item.categories.map((category) => category.name).join(", ") || "General"
  }

  announcementIcon(item: IAnnouncement): string {
    const text = this.categoryNames(item).toLowerCase()
    if (/housing|dorm|accommodation/.test(text)) return "bed"
    if (text.includes("event")) return "event"
    if (/maint|repair/.test(text)) return "build"
    if (text.includes("deadline")) return "schedule"
    if (text.includes("urgent")) return "warning"
    return "campaign"
  }
}
